import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Feather, Trees, Scale, ScrollText, Crown, Sparkles,
  Shield, HeartHandshake, Flame, Wand2, BookOpenText, Music,
} from "lucide-react";
import { TRADITIONS } from "@/constants/testIds";
import { useLang } from "@/lib/lang";
import Oracles from "@/components/Oracles";

// Curated static content — no LLM needed. All from Old Irish / Gallo-Roman
// scholarly consensus (Ó hÓgáin, Kelly, Green, Le Roux).
const ROLES = [
  {
    id: "druide",
    icon: Trees,
    accent: "#a67bff",
    fr: { title: "Le Druide (Draoi)", role: "Prêtre-philosophe · astronome · juge sacré",
          body: "Sommet spirituel de la société celte. Le druide (Draoi) préside les rites du Nemeton (bois sacré), lit le calendrier de Coligny, interprète le vol des oiseaux, transmet oralement la sagesse. En Irlande ancienne, il conseille l'Ard-Rí (haut-roi) et peut opposer son verbe à la parole royale. Cinq à vingt ans de mémorisation avant l'initiation.",
          purpose: "Garder le lien entre le monde-du-milieu et l'Autre-Monde (Sídhe). Faire mémoire et donner sens." },
    en: { title: "The Druid (Draoi)", role: "Priest-philosopher · astronomer · sacred judge",
          body: "Spiritual summit of Celtic society. The Druid presides over Nemeton (sacred grove) rites, reads the Coligny calendar, interprets bird flight, transmits wisdom orally. In ancient Ireland, they counsel the Ard-Rí (high king) and can oppose royal speech. Five to twenty years of memorisation before initiation.",
          purpose: "Bridge the middle world and the Otherworld (Sídhe). Hold memory and give meaning." },
  },
  {
    id: "vate",
    icon: Sparkles,
    accent: "#7dd3c0",
    fr: { title: "Le Vate (Fáith)", role: "Voyant · guérisseur émotionnel · devin sacré",
          body: "Membre du triple collège barde-vate-druide. Le vate (fáith en vieil irlandais) lit les signes de l'âme, panse les blessures invisibles, pratique l'incubation (imbas forosnai — l'illumination par le silence). Sur AstroVoyance, il est réinterprété comme coach relationnel et émotionnel, sans jamais prédire.",
          purpose: "Voir ce que l'œil quotidien ne voit pas. Restaurer l'intégrité intérieure." },
    en: { title: "The Vate (Fáith)", role: "Seer · emotional healer · sacred diviner",
          body: "Member of the triple bardic-vate-druidic college. The vate (fáith in Old Irish) reads soul-signs, mends invisible wounds, practices incubation (imbas forosnai — illumination through silence). On AstroVoyance, reinterpreted as relational & emotional coach, never predicting.",
          purpose: "See what the everyday eye misses. Restore inner integrity." },
  },
  {
    id: "barde",
    icon: Music,
    accent: "#f5d572",
    fr: { title: "Le Barde (Bard)", role: "Poète · chanteur · gardien de la mémoire",
          body: "Le barde chante généalogies, exploits, satires. En Irlande, la classe filid englobe les bardes (compositeurs) et les fili (poètes-magiciens). Sa satire (áer) pouvait défigurer physiquement — les rois la redoutaient. Le barde transmettait aussi l'humour celtique et la moquerie sacrée envers la prétention.",
          purpose: "Faire durer les visages, punir la démesure par le rire, ancrer la mémoire tribale." },
    en: { title: "The Bard", role: "Poet · singer · memory keeper",
          body: "The bard chants genealogies, deeds, satires. In Ireland the filid class encompasses bards (composers) and fili (poet-magicians). Their satire (áer) could physically disfigure — kings feared it. Bards also carried Celtic humor and sacred mockery of pretension.",
          purpose: "Make faces last, punish hubris through laughter, anchor tribal memory." },
  },
  {
    id: "fili",
    icon: Feather,
    accent: "#d6c1ff",
    fr: { title: "Le Fili (poète-magicien)", role: "Poète savant · rêveur professionnel · haute-satire",
          body: "Le fili occupe le sommet de l'ordre poétique irlandais — bien au-dessus du simple bard. Douze ans d'études : mètres, mythologies, généalogies, lois. Il pratique le tarbfeis (rêve du taureau) et l'imbas forosnai. Sa parole a force de loi et de malédiction.",
          purpose: "Traverser les frontières du visible par le mot juste. Servir la vérité poétique." },
    en: { title: "The Fili (poet-magician)", role: "Learned poet · professional dreamer · high satire",
          body: "The fili sits atop the Irish poetic order — far above the mere bard. Twelve years of study: meters, myths, genealogies, laws. Practices tarbfeis (bull-dream) and imbas forosnai. Their word has force of law and curse.",
          purpose: "Cross visible-world boundaries by the exact word. Serve poetic truth." },
  },
  {
    id: "brehon",
    icon: Scale,
    accent: "#c8f76c",
    fr: { title: "Le Brehon (Breithemh)", role: "Juge · gardien du droit coutumier",
          body: "Le breithemh administre le corpus juridique des Brehon Laws (Senchas Már, Bretha Nemed). Justice restaurative : le tort se répare par éraic (prix du sang) et compensation, non par vengeance. Chaque personne a un enech (visage/honneur) chiffré selon son statut. Femmes, servants, poètes, druides : tous ont place dans la loi.",
          purpose: "Maintenir l'équilibre social par la parole juste et la réparation, non le châtiment." },
    en: { title: "The Brehon (Breithemh)", role: "Judge · keeper of customary law",
          body: "The breithemh administers the Brehon Laws corpus (Senchas Már, Bretha Nemed). Restorative justice: wrongs are repaired by éraic (blood-price) and compensation, not vengeance. Each person has an enech (face/honour) priced by status. Women, servants, poets, druids: all have standing in the law.",
          purpose: "Maintain social balance through just speech and restoration, not punishment." },
  },
  {
    id: "ollamh",
    icon: BookOpenText,
    accent: "#f0d97a",
    fr: { title: "L'Ollamh", role: "Maître-professeur · docteur suprême d'un art",
          body: "Sommet académique — ollamh signifie littéralement « le plus grand ». Un ollamh en poésie connaît 350 récits par cœur, tous les mètres, toute la loi. Il siège à la droite du roi, boit dans sa coupe, porte une robe d'écarlate. Titre décerné après 21 ans d'études officielles.",
          purpose: "Incarner la connaissance dans un corps — pour qu'elle traverse les générations." },
    en: { title: "The Ollamh", role: "Master-teacher · supreme doctor of an art",
          body: "Academic summit — ollamh literally means 'greatest'. An ollamh in poetry knows 350 tales by heart, all meters, all law. Sits at the king's right, drinks from his cup, wears scarlet robes. Title conferred after 21 years of formal study.",
          purpose: "Embody knowledge in a body — so it may cross generations." },
  },
  {
    id: "ban-drui",
    icon: Wand2,
    accent: "#f8b4d9",
    fr: { title: "La Ban-Drui (druidesse)", role: "Prêtresse-guerrière · voyante · reine sacrée",
          body: "Les femmes celtes accèdent à tous les ordres sacrés. La Ban-Drui préside les rites lunaires, initie aux mystères de Brigid (poésie, forge, guérison). Boadicée (Reine des Icènes) est la figure la plus connue, mais Fedelm la Prophétesse et Scáthach la Maîtresse-d'armes sont majeures dans la geste irlandaise.",
          purpose: "Rappeler que le sacré n'a pas de genre — et que la guerrière peut aussi être visionnaire." },
    en: { title: "The Ban-Drui (druidess)", role: "Priestess-warrior · seer · sacred queen",
          body: "Celtic women access all sacred orders. The Ban-Drui presides over lunar rites, initiates in Brigid's mysteries (poetry, forge, healing). Boudica (Iceni Queen) is best known, but Fedelm the Prophetess and Scáthach the Weapon-Mistress are major in Irish lore.",
          purpose: "Remind that the sacred has no gender — and the warrior can also be visionary." },
  },
  {
    id: "ard-ri",
    icon: Crown,
    accent: "#d4af37",
    fr: { title: "L'Ard-Rí (haut-roi)", role: "Roi suprême · époux symbolique de la terre",
          body: "L'Ard-Rí (« haut-roi ») siège à Tara. Il n'est légitime que s'il épouse rituellement la Souveraineté (Flaith) — déesse de la terre d'Irlande. Un roi injuste rend le pays stérile. Il est soumis aux geasa (interdits sacrés personnels) : Cormac mac Airt tomba pour en avoir rompu un.",
          purpose: "Servir la terre. Le roi n'est pas propriétaire — il est époux temporaire." },
    en: { title: "The Ard-Rí (high king)", role: "Supreme king · symbolic spouse of the land",
          body: "The Ard-Rí ('high king') sits at Tara. Only legitimate if he ritually marries Sovereignty (Flaith) — goddess of Ireland's land. An unjust king renders the country barren. Bound by geasa (personal sacred taboos): Cormac mac Airt fell for breaking one.",
          purpose: "Serve the land. The king is not owner — but temporary spouse." },
  },
  {
    id: "fianna",
    icon: Shield,
    accent: "#e07a4f",
    fr: { title: "Le Fianna (guerrier)", role: "Ordre guerrier · protecteur · chasseur initiatique",
          body: "Les Fianna sont des bandes guerrières hors-clans qui défendent l'Irlande sous Fionn mac Cumhaill. Ils apprennent 12 arts : combat, poésie, chasse, échecs celtiques, résistance à la faim, saut par-dessus une branche à hauteur de front. Ils vivent l'été dans les forêts, l'hiver chez le roi.",
          purpose: "Défendre les frontières visibles ET intérieures. Rester digne dans l'épreuve." },
    en: { title: "The Fianna (warrior)", role: "Warrior order · protector · initiatic hunter",
          body: "The Fianna are clan-less warrior bands defending Ireland under Fionn mac Cumhaill. They learn 12 arts: combat, poetry, hunting, Celtic chess, hunger endurance, leaping over a forehead-high branch. Summer in the forests, winter at the king's hall.",
          purpose: "Defend outer AND inner borders. Remain dignified under trial." },
  },
  {
    id: "guerisseur",
    icon: HeartHandshake,
    accent: "#7dd3c0",
    fr: { title: "Le Lia (guérisseur)", role: "Médecin-herboriste · chirurgien de guerre",
          body: "Les Lianna (guérisseurs) forment un ordre distinct des druides. Ils opèrent au tuath (fontaine sacrée) et connaissent 365 plantes. Dian Cécht, dieu-médecin des Tuatha Dé Danann, restaura le bras d'argent de Nuada. Chaque tuath (petit royaume) devait avoir son lia.",
          purpose: "Restaurer le corps quand la parole du barde ou le rite du druide ne suffisent plus." },
    en: { title: "The Lia (healer)", role: "Physician-herbalist · war surgeon",
          body: "The Lianna (healers) form a distinct order from druids. They operate at the tuath (sacred spring) and know 365 plants. Dian Cécht, physician-god of the Tuatha Dé Danann, restored Nuada's silver arm. Every tuath (small kingdom) had to have its lia.",
          purpose: "Restore the body when the bard's word or druid's rite are no longer enough." },
  },
  {
    id: "farfadet",
    icon: Flame,
    accent: "#c8f76c",
    fr: { title: "Le Farfadet / Leprechaun", role: "Petit peuple · comique sacré · gardien du seuil",
          body: "Les leprechauns (Leipreacháin) sont issus du Sídhe (Autre-Monde). Cordonniers pour les fées, ils cachent un chaudron d'or au pied de l'arc-en-ciel. Malicieux, moqueurs, ils punissent la cupidité par des tours absurdes. Dans la tradition, le rire du farfadet libère ce que la sagesse enferme.",
          purpose: "Faire rire pour rappeler que rien n'est sacré au point d'échapper à la moquerie tendre." },
    en: { title: "The Farfadet / Leprechaun", role: "Fair folk · sacred comic · threshold-keeper",
          body: "Leprechauns (Leipreacháin) come from the Sídhe (Otherworld). Cobblers for the fairies, they hide a cauldron of gold at rainbow's foot. Mischievous, mocking, they punish greed with absurd tricks. Traditionally, the farfadet's laughter frees what wisdom locks away.",
          purpose: "Make you laugh to remind you nothing is so sacred as to escape tender mockery." },
  },
  {
    id: "scop",
    icon: ScrollText,
    accent: "#b5a25e",
    fr: { title: "Le Sencha (chroniqueur)", role: "Historien officiel · gardien des généalogies",
          body: "Le sencha (« ancien ») conserve la mémoire longue : généalogies royales, saga des invasions (Lebor Gabála Érenn), noms de tous les lieux (dindshenchas). Il n'invente rien — il transmet. Le Sencha Már (grand recueil de droit) porte son nom. Sans lui, les autres arts n'ont pas de sol.",
          purpose: "Ancrer le présent dans une longue mémoire, pour que rien d'important ne se perde." },
    en: { title: "The Sencha (chronicler)", role: "Official historian · genealogy-keeper",
          body: "The sencha ('elder') keeps long memory: royal genealogies, invasion sagas (Lebor Gabála Érenn), place-name lore (dindshenchas). Invents nothing — transmits. The Senchas Már (grand law compendium) bears their name. Without them, other arts have no ground.",
          purpose: "Anchor the present in long memory, so nothing important is lost." },
  },
];

export default function Traditions() {
  const { lang, t, setLang } = useLang();

  useEffect(() => {
    if (window.location.hash === "#oracles") {
      setTimeout(() => document.getElementById("oracles")?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, []);

  return (
    <div data-testid={TRADITIONS.page} className="trad-root min-h-screen relative">
      <div className="trad-bg" aria-hidden="true" />

      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "var(--gold)" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
          </Link>
          <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
               style={{ background: "rgba(107,63,160,0.25)", border: "1px solid rgba(214,193,255,0.25)" }}>
            <button onClick={() => setLang("fr")} type="button" className="px-2.5 py-1.5"
                    style={{ background: lang === "fr" ? "var(--gold)" : "transparent", color: lang === "fr" ? "var(--void)" : "var(--violet-soft)", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
            <button onClick={() => setLang("en")} type="button" className="px-2.5 py-1.5"
                    style={{ background: lang === "en" ? "var(--gold)" : "transparent", color: lang === "en" ? "var(--void)" : "var(--violet-soft)", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-10">
        <div className="overline mb-4">
          <span className="kicker-line" />
          {lang === "fr" ? "Traditions celtes · rôles sacrés de l'Irlande ancienne" : "Celtic traditions · sacred roles of ancient Ireland"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
          {lang === "fr" ? (
            <>Qui faisait <span className="italic" style={{ color: "var(--gold-glow)" }}>quoi</span> près du feu ?</>
          ) : (
            <>Who did <span className="italic" style={{ color: "var(--gold-glow)" }}>what</span> by the fire?</>
          )}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "var(--violet-soft)" }}>
          {lang === "fr"
            ? "Douze figures centrales de l'Irlande ancienne — Druide, Vate, Barde, Fili, Brehon, Ollamh, Ban-Drui, Ard-Rí, Fianna, Lia, Farfadet, Sencha — et leur rôle exact dans la trame sociale et spirituelle celte. Un aide-mémoire respectueux, sans New Age."
            : "Twelve central figures of ancient Ireland — Druid, Vate, Bard, Fili, Brehon, Ollamh, Ban-Drui, Ard-Rí, Fianna, Lia, Farfadet, Sencha — and their exact role in Celtic social and spiritual fabric. A respectful primer, no New Age."}
        </p>
      </section>

      <div className="relative z-10">
        <Oracles />
      </div>

      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const t = r[lang] || r.fr;
            return (
              <article key={r.id} data-testid={TRADITIONS.card(r.id)} className="trad-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center"
                       style={{ background: `${r.accent}22`, border: `1px solid ${r.accent}66` }}>
                    <Icon size={18} style={{ color: r.accent }} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl leading-tight" style={{ color: "#fff5df" }}>{t.title}</h3>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: r.accent }}>{t.role}</div>
                  </div>
                </div>
                <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--violet-soft)" }}>{t.body}</p>
                <div className="text-xs italic" style={{ color: "var(--muted)" }}>
                  <span className="font-mono uppercase tracking-widest text-[10px] not-italic" style={{ color: r.accent }}>
                    {lang === "fr" ? "But sacré" : "Sacred purpose"} ·
                  </span>{" "}{t.purpose}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-10 text-xs font-mono" style={{ color: "var(--muted)" }}>
          {lang === "fr"
            ? "Sources : Fergus Kelly, A Guide to Early Irish Law ; Daíthí Ó hÓgáin, Myth Legend & Romance ; Miranda Green, The World of the Druids ; Françoise Le Roux, La Civilisation celtique. Aucune source New Age. Respect scholarly."
            : "Sources: Fergus Kelly, A Guide to Early Irish Law; Daíthí Ó hÓgáin, Myth Legend & Romance; Miranda Green, The World of the Druids; Françoise Le Roux, La Civilisation celtique. No New Age sources. Scholarly respect."}
        </div>

        {/* Greek-Celtic links */}
        <div className="mt-14 p-6 sm:p-8 rounded-2xl"
             data-testid="traditions-greek-celtic"
             style={{
               background: "linear-gradient(180deg, rgba(28,36,70,0.75), rgba(14,18,44,0.85))",
               border: "1px solid rgba(100,160,255,0.3)",
             }}>
          <div className="overline mb-3" style={{ color: "#64a0ff" }}>
            <span className="kicker-line" />
            {lang === "fr" ? "Pont historique · Grecs & Celtes irlandais" : "Historical bridge · Greeks & Irish Celts"}
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl mb-4" style={{ color: "#f0f5ff" }}>
            {lang === "fr" ? "Les bons liens entre les Grecs et les Celtes" : "The Deep Ties Between Greeks and Celts"}
          </h2>
          <div className="grid gap-4 md:grid-cols-2 text-sm leading-relaxed" style={{ color: "#c9d8f5" }}>
            <p>
              {lang === "fr"
                ? "Dès le VIIe-VIe siècle av. J.-C., les Grecs ioniens commercent avec les Celtes continentaux. En 600 av. J.-C., les Phocéens fondent Massalia (Marseille) ; en 215 av. J.-C., Hannibal recrute Gaulois et Grecs côte à côte. Posidonius d'Apamée (Ier s. av. J.-C.) vit chez les druides et laisse les témoignages les plus riches sur leur science."
                : "From the 7th-6th century BCE, Ionian Greeks traded with continental Celts. In 600 BCE the Phocaeans founded Massalia (Marseille); in 215 BCE Hannibal recruited Gauls and Greeks side by side. Posidonius of Apamea (1st c. BCE) lived among the Druids and left the richest surviving accounts of their science."}
            </p>
            <p>
              {lang === "fr"
                ? "Les cosmologies se répondent : les Hespérides grecs ↔ Avalon irlandais (îles-pommes où règne l'immortalité) ; Prométhée ↔ Lugh (porteurs de feu et d'arts) ; les Muses ↔ les Fili (poètes-magiciens inspirés) ; les 4 éléments d'Empédocle ↔ les 4 trésors des Tuatha Dé Danann. Même structure indo-européenne, deux ciselures distinctes."
                : "The cosmologies echo each other: Greek Hesperides ↔ Irish Avalon (apple-islands of immortality); Prometheus ↔ Lugh (bringers of fire and crafts); the Muses ↔ the Fili (inspired poet-magicians); Empedocles' 4 elements ↔ the 4 treasures of the Tuatha Dé Danann. Same Indo-European structure, two distinct chisels."}
            </p>
            <p>
              {lang === "fr"
                ? "Philosophiquement, les deux peuples honorent l'immortalité de l'âme (Platon dans le Phédon ↔ Diodore de Sicile sur les druides), la métempsycose (Pythagore ↔ enseignement druidique rapporté par César), la primauté du chêne et la science des étoiles. Les druides enseignaient en grec à certains disciples (Jules César, Guerre des Gaules VI, 14)."
                : "Philosophically both peoples honor the soul's immortality (Plato in the Phaedo ↔ Diodorus Siculus on the Druids), metempsychosis (Pythagoras ↔ Druidic teaching reported by Caesar), the primacy of the oak, and the science of stars. Druids taught in Greek to certain students (Caesar, Gallic War VI, 14)."}
            </p>
            <p>
              {lang === "fr"
                ? "Côté éthique, le « connais-toi toi-même » (gnōthi seauton, Delphes) rejoint directement l'injonction druidique rapportée par Diogène Laërce : « honorer les dieux, ne rien faire de mal, exercer sa valeur ». Trois piliers presque identiques — qui résonneront aussi dans les trois piliers zoroastriens d'Ahura Mazda."
                : "Ethically, the \"know thyself\" (gnōthi seauton, Delphi) directly rhymes with the Druidic injunction reported by Diogenes Laërtius: \"honor the gods, do nothing evil, exercise valor.\" Three near-identical pillars — which will also echo in the three Zoroastrian pillars of Ahura Mazda."}
            </p>
          </div>
          <div className="mt-5">
            <a href="/amis-des-celtes"
               data-testid="traditions-amis-cta"
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-mono uppercase tracking-[0.18em] transition-all"
               style={{
                 background: "linear-gradient(135deg, #64a0ff, #4a7fcf)",
                 color: "#040812",
                 fontWeight: 600,
               }}>
              {lang === "fr" ? "⚡ Rencontrer les Amis des Celtes" : "⚡ Meet the Celts' Friends"}
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .trad-root { background: radial-gradient(ellipse at top, #14082a 0%, #080418 55%, #030109 100%); color: #f4ead0; overflow-x: hidden; }
        .trad-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(circle at 20% 30%, rgba(212,175,55,0.10) 0%, transparent 45%),
            radial-gradient(circle at 82% 74%, rgba(166,123,255,0.10) 0%, transparent 45%),
            repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 92px),
            linear-gradient(180deg, #10082a 0%, #030109 100%); }
        .trad-card { background: linear-gradient(180deg, rgba(20,10,44,0.85) 0%, rgba(10,4,24,0.94) 100%); border: 1px solid rgba(214,193,255,0.18); border-radius: 22px; padding: 22px; transition: transform 240ms ease, border-color 240ms ease, box-shadow 240ms ease; }
        .trad-card:hover { transform: translateY(-3px); border-color: rgba(212,175,55,0.5); box-shadow: 0 24px 48px -20px rgba(166,123,255,0.35); }
      `}</style>
    </div>
  );
}
